import { Link } from 'react-router-dom';
import { Alert } from '@shared/components';
import { statusLabel } from '@/features/agreements';
import type { OwnDealerLiveAgreement } from '@/features/agreements';
import type { ExistingAgreementCheckState } from '../../../hooks/useExistingAgreementCheck';

interface ExistingAgreementNoticeProps {
    checkState: ExistingAgreementCheckState;
    canViewAgreement: boolean;
    variant?: 'details' | 'lookup';
}

const NEUTRAL_CLASSES = 'bg-mp-background border border-mp-border rounded-lg p-4 text-sm text-mp-foreground';

function AgreementReference({
    agreement,
    canViewAgreement,
}: {
    agreement: OwnDealerLiveAgreement;
    canViewAgreement: boolean;
}) {
    return (
        <li>
            {canViewAgreement ? (
                <Link
                    to={`/agreements/${agreement.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline"
                >
                    {agreement.agreementRef}
                </Link>
            ) : (
                <span className="font-medium">{agreement.agreementRef}</span>
            )}{' '}
            ({statusLabel(agreement.status)})
        </li>
    );
}

export function ExistingAgreementNotice({
    checkState,
    canViewAgreement,
    variant = 'details',
}: ExistingAgreementNoticeProps) {
    if (variant === 'lookup' && checkState.state === 'checking') {
        return (
            <div role="status" className={NEUTRAL_CLASSES}>
                Checking for an existing plan…
            </div>
        );
    }

    if (variant === 'lookup' && checkState.state === 'result' && !checkState.check.hasLiveAgreement) {
        return (
            <div role="status" className={NEUTRAL_CLASSES}>
                No live plan found for this vehicle.
            </div>
        );
    }

    if (checkState.state === 'error') {
        return (
            <div role="status" className={NEUTRAL_CLASSES}>
                We couldn&apos;t check for an existing plan on this vehicle. Check Agreements before continuing.
            </div>
        );
    }

    if (checkState.state !== 'result' || !checkState.check.hasLiveAgreement) return null;

    const { ownDealer, otherDealer } = checkState.check;

    return (
        <Alert variant="warning" role="status">
            <div className="space-y-2">
                {ownDealer.length > 0 && (
                    <div>
                        <p className="font-medium">This vehicle already has a live plan</p>
                        <ul className="list-disc pl-5">
                            {ownDealer.map((agreement) => (
                                <AgreementReference
                                    key={agreement.id}
                                    agreement={agreement}
                                    canViewAgreement={canViewAgreement}
                                />
                            ))}
                        </ul>
                    </div>
                )}
                {otherDealer && (
                    <p>
                        This vehicle has a live plan with another dealer. Advise your customer to contact MotorPlan to
                        discuss their existing plan.
                    </p>
                )}
            </div>
        </Alert>
    );
}
